import { z } from 'zod';
import {
	createRemoteJWKSet,
	customFetch,
	jwtVerify,
	type JWTVerifyOptions,
} from 'jose';

// ─── Contract versioning ────────────────────────────────────────────────────

export const AUTH_CONTRACT_VERSION = 1 as const;
export const SUPPORTED_AUTH_CONTRACT_VERSIONS = [1] as const;

export class UnsupportedAuthContractVersionError extends Error {
	readonly code = 'unsupported_contract_version';

	constructor(readonly receivedVersion: unknown) {
		super(`Unsupported auth contract version: ${String(receivedVersion)}`);
		this.name = 'UnsupportedAuthContractVersionError';
	}
}

export function assertSupportedAuthContractVersion(value: unknown): 1 {
	if (value !== AUTH_CONTRACT_VERSION) {
		throw new UnsupportedAuthContractVersionError(value);
	}
	return value;
}

// ─── Token conventions ───────────────────────────────────────────────────────

/** `typ` protected-header value for Blazorly access tokens. */
export const ACCESS_TOKEN_TYPE = 'blazorly-access+jwt' as const;
/** `tokenUse` claim value for user access tokens (distinct from service/agent tokens). */
export const TOKEN_USE_ACCESS = 'access' as const;
/** Refresh-token cookie name (agent worker convention, adopted platform-wide). */
export const REFRESH_COOKIE_NAME = 'blazorly-refresh' as const;

/**
 * `iss`/`aud` of the future dedicated identity worker (Phase 2). Verifiers that
 * accept remote JWKS tokens must be configured with this issuer once the
 * `blazorly-auth` service starts issuing; until then legacy issuers apply.
 */
export const AUTH_SERVICE_ISSUER = 'blazorly-auth' as const;

/**
 * Issuers whose tokens are already circulating today. A migration-period
 * verifier accepts the union of these plus AUTH_SERVICE_ISSUER, then the
 * legacy entries are removed once re-issuance completes.
 */
export const LEGACY_ISSUERS = ['blazorly-agent'] as const;

// ─── Canonical access-token claims ───────────────────────────────────────────

/**
 * The single target claim shape every issuer converges on. It is the agent
 * worker's current shape (sub/sid/sev/tokenUse/email) — the backend's
 * userId/sessionId variant is normalized into it rather than the reverse,
 * because sub/sid/iss/aud are the registered JWT names third-party libraries
 * already understand.
 *
 * Deliberately not `.strict()`: registered claims (`iss`, `aud`, `jti`, `nbf`)
 * are validated by `jwtVerify` before normalization and simply stripped from
 * the canonical result. Anything semantically load-bearing (e.g. `tokenUse`)
 * is a literal field below.
 */
export const accessTokenClaimsSchema = z
	.object({
		sub: z.uuid(),
		/** Session id — bound to the `sessions` row the token belongs to. */
		sid: z.string().min(1).max(200),
		email: z.email(),
		/**
		 * Org whose tenant DB holds the session (DT-1), as a uuid string.
		 * Optional only for migration-period readers; issuers always stamp it
		 * and org-scoped verifiers fail closed when it is absent.
		 */
		org: z.uuid().optional(),
		/** Session epoch; bumped to invalidate already-issued access tokens. */
		sev: z.number().int().min(0),
		tokenUse: z.literal(TOKEN_USE_ACCESS),
		nonce: z.string().min(1).max(200).optional(),
		iat: z.number().int().nonnegative(),
		exp: z.number().int().positive(),
	});

export type AccessTokenClaims = z.infer<typeof accessTokenClaimsSchema>;

/**
 * Backend (Hono API) hand-rolled HS256 payload. Read-shape only: this schema
 * never validates new tokens for issuance; it exists so a migration-period
 * verifier can accept and normalize backend-issued tokens.
 */
export const legacyBackendClaimsSchema = z
	.object({
		userId: z.uuid(),
		sessionId: z.string().min(1).max(200),
		email: z.email(),
		nonce: z.string().min(1).max(200).optional(),
		iat: z.number().int().nonnegative(),
		exp: z.number().int().positive(),
	})
	.strict()
	.transform((legacy) => ({
		sub: legacy.userId,
		sid: legacy.sessionId,
		email: legacy.email,
		sev: 0,
		tokenUse: TOKEN_USE_ACCESS,
		...(legacy.nonce !== undefined ? { nonce: legacy.nonce } : {}),
		iat: legacy.iat,
		exp: legacy.exp,
	}) satisfies AccessTokenClaims);

export class UnsupportedAccessTokenClaimsError extends Error {
	readonly code = 'unsupported_access_token_claims';

	constructor() {
		super('Verified JWT payload does not match any supported Blazorly access-token claim shape.');
		this.name = 'UnsupportedAccessTokenClaimsError';
	}
}

/**
 * Normalize a *signature-verified* JWT payload into canonical claims.
 * Accepts the canonical shape and the legacy backend shape; anything else is
 * rejected so unrelated token types (internal service tokens, agent DO tokens,
 * OAuth assertions) cannot be smuggled through as user access tokens.
 */
export function normalizeAccessTokenClaims(payload: unknown): AccessTokenClaims {
	const canonical = accessTokenClaimsSchema.safeParse(payload);
	if (canonical.success) return canonical.data;
	const legacy = legacyBackendClaimsSchema.safeParse(payload);
	if (legacy.success) return legacy.data;
	throw new UnsupportedAccessTokenClaimsError();
}

// ─── Verifier interface and implementations ─────────────────────────────────

export class InvalidAccessTokenError extends Error {
	readonly code = 'invalid_access_token';

	constructor(
		message: string,
		readonly status: 401 | 500 = 401,
		options?: { cause?: unknown },
	) {
		super(message, options);
		this.name = 'InvalidAccessTokenError';
	}
}

/**
 * Manager-agnostic token verification surface. Implementations verify the
 * signature and issuer/audience, then return *canonical* claims — callers
 * never branch on which issuer produced a token.
 */
export interface AccessTokenVerifier {
	verify(token: string): Promise<AccessTokenClaims>;
}

export interface LocalSecretVerifierOptions {
	/** Shared HMAC secret (JWT_SECRET today). */
	secret: string;
	/**
	 * Accepted `iss` values; pass {@link LEGACY_ISSUERS} for agent/crew.
	 * Omit to also accept the legacy backend shape, which carries no `iss`
	 * and is then constrained only by the shared secret and claims shape.
	 */
	issuer?: string | string[];
	/** Accepted `aud` values; see `issuer`. */
	audience?: string | string[];
	clockToleranceSeconds?: number;
}

/**
 * Transition-period verifier for the current symmetric (HS256) tokens.
 * Once the dedicated identity worker issues via JWKS, services should move to
 * {@link createRemoteJwksVerifier} and drop their copy of the signing secret.
 */
export function createLocalSecretVerifier(options: LocalSecretVerifierOptions): AccessTokenVerifier {
	const key = new TextEncoder().encode(options.secret);
	const verifyOptions: JWTVerifyOptions = {
		algorithms: ['HS256'],
		...(options.issuer !== undefined ? { issuer: options.issuer } : {}),
		...(options.audience !== undefined ? { audience: options.audience } : {}),
		...(options.clockToleranceSeconds !== undefined
			? { clockTolerance: options.clockToleranceSeconds }
			: {}),
	};
	return {
		async verify(token: string): Promise<AccessTokenClaims> {
			let payload: unknown;
			try {
				({ payload } = await jwtVerify(token, key, verifyOptions));
			} catch (error) {
				throw new InvalidAccessTokenError('Access token rejected.', 401, { cause: error });
			}
			try {
				return normalizeAccessTokenClaims(payload);
			} catch (error) {
				throw new InvalidAccessTokenError('Access token has unsupported claims.', 401, {
					cause: error,
				});
			}
		},
	};
}

export interface RemoteJwksVerifierOptions {
	/** JWKS endpoint, e.g. `https://auth.blazorly.com/.well-known/jwks.json`. */
	jwksUrl: string;
	/** Expected `iss`. Use {@link AUTH_SERVICE_ISSUER} for the dedicated service. */
	issuer: string | string[];
	/** Expected `aud`; each service passes its own identifier. */
	audience: string | string[];
	/** Allowed asymmetric algorithms; defaults to the supported set. */
	algorithms?: JWTVerifyOptions['algorithms'];
	clockToleranceSeconds?: number;
	/** Injectable fetch for tests and Workers `fetch` binding pinning. */
	fetchImpl?: typeof fetch;
}

/**
 * Target-architecture verifier: asymmetric tokens verified against a remote
 * JWKS. Services hold only public keys — a compromised service worker can no
 * longer forge tokens for anything else.
 */
export function createRemoteJwksVerifier(options: RemoteJwksVerifierOptions): AccessTokenVerifier {
	// jose v6 exports no JWKS type name; the remote set's runtime shape is an
	// opaque key resolver accepted directly by jwtVerify.
	const jwks = createRemoteJWKSet(new URL(options.jwksUrl), options.fetchImpl
		? { [customFetch]: options.fetchImpl }
		: {});
	const verifyOptions: JWTVerifyOptions = {
		algorithms: options.algorithms ?? ['EdDSA', 'ES256', 'RS256'],
		issuer: options.issuer,
		audience: options.audience,
		...(options.clockToleranceSeconds !== undefined
			? { clockTolerance: options.clockToleranceSeconds }
			: {}),
	};
	return {
		async verify(token: string): Promise<AccessTokenClaims> {
			let payload: unknown;
			try {
				({ payload } = await jwtVerify(token, jwks, verifyOptions));
			} catch (error) {
				throw new InvalidAccessTokenError('Access token rejected.', 401, { cause: error });
			}
			try {
				return normalizeAccessTokenClaims(payload);
			} catch (error) {
				throw new InvalidAccessTokenError('Access token has unsupported claims.', 401, {
					cause: error,
				});
			}
		},
	};
}

/**
 * Migration-period composite: tries each verifier in order and returns the
 * first success. Use it to accept legacy HS256 tokens and new JWKS tokens
 * simultaneously, then remove the legacy entries once token TTLs expire.
 */
export function createCompositeVerifier(verifiers: AccessTokenVerifier[]): AccessTokenVerifier {
	if (verifiers.length === 0) {
		throw new Error('createCompositeVerifier requires at least one verifier.');
	}
	return {
		async verify(token: string): Promise<AccessTokenClaims> {
			let lastError: InvalidAccessTokenError | undefined;
			for (const verifier of verifiers) {
				try {
					return await verifier.verify(token);
				} catch (error) {
					if (error instanceof InvalidAccessTokenError && error.status === 500) throw error;
					lastError = error instanceof InvalidAccessTokenError
						? error
						: new InvalidAccessTokenError('Access token rejected.', 401, { cause: error });
				}
			}
			throw lastError ?? new InvalidAccessTokenError('Access token rejected.');
		},
	};
}

// ─── Authenticated user and session wire shapes ─────────────────────────────

/**
 * Read-shapes are deliberately loose (extra fields tolerated, additive fields
 * optional) so issuing services can evolve payloads without a coordinated
 * contract release. Write-shapes elsewhere in the platform stay strict.
 */
export const organizationMembershipSchema = z.looseObject({
	id: z.string().min(1),
	role: z.string().min(1),
});

export const authenticatedUserSchema = z.looseObject({
	id: z.uuid(),
	email: z.email(),
	// Nullable: backend-created users (notably OAuth signups) can have a NULL
	// display_name, and the platform SSO surface passes it through verbatim.
	// Consumers default null to an email-derived name (see builder's
	// toBuilderUser).
	displayName: z.string().nullable(),
	username: z.string().nullable().optional(),
	avatarUrl: z.string().nullable().optional(),
	provider: z.string().min(1).optional(),
	emailVerified: z.boolean().optional(),
	twoFactorEnabled: z.boolean().optional(),
	// Nullable by design: resolvePlatformAuthorization returns null when the
	// user is not a member of the configured platform organization (backend-
	// created accounts before org assignment, non-uuid principals).
	platformRole: z.string().nullable().optional(),
	organizations: z.array(organizationMembershipSchema).optional(),
});

export type AuthenticatedUser = z.infer<typeof authenticatedUserSchema>;

export const authSessionSchema = z.looseObject({
	token: z.string().min(1),
	expiresIn: z.number().int().positive(),
	user: authenticatedUserSchema,
});

export type AuthSession = z.infer<typeof authSessionSchema>;

export const tokenRefreshSchema = z.looseObject({
	token: z.string().min(1),
	expiresIn: z.number().int().positive(),
	user: authenticatedUserSchema.optional(),
});

export type TokenRefresh = z.infer<typeof tokenRefreshSchema>;

export const passwordLoginRequestSchema = z
	.object({
		email: z.email(),
		password: z.string().min(1).max(480_000),
		/**
		 * Chosen organization when the email maps to multiple directory rows
		 * (DT-1). Absent on first attempt; set from the 409 organization
		 * selection response.
		 */
		organizationId: z.uuid().optional(),
	})
	.strict();

export type PasswordLoginRequest = z.infer<typeof passwordLoginRequestSchema>;

// ─── Multi-org login selection (DT-1) ───────────────────────────────────────

/** `error.code` value when a login needs an explicit organization choice. */
export const ORGANIZATION_SELECTION_REQUIRED = 'organization_selection_required' as const;

export const loginOrganizationOptionSchema = z.looseObject({
	id: z.uuid(),
	name: z.string().min(1),
	agentProvisioningStatus: z.string().min(1),
});

export type LoginOrganizationOption = z.infer<typeof loginOrganizationOptionSchema>;

/** Shape of the 409 body when the account exists in several organizations. */
export const organizationSelectionRequiredSchema = z.looseObject({
	error: z.looseObject({ code: z.literal(ORGANIZATION_SELECTION_REQUIRED) }),
	organizations: z.array(loginOrganizationOptionSchema).min(1),
});

/**
 * Extract the selectable organizations from a failed
 * {@link AuthClient.login} call, or null when the failure was anything else.
 */
export function organizationSelectionOptions(error: AuthClientError): LoginOrganizationOption[] | null {
	if (error.status !== 409 || error.body === null || typeof error.body !== 'object') return null;
	const parsed = organizationSelectionRequiredSchema.safeParse(error.body);
	return parsed.success ? parsed.data.organizations : null;
}

// ─── Organization switching (DT-1) ───────────────────────────────────────────

export const switchOrgRequestSchema = z
	.object({
		organizationId: z.uuid(),
	})
	.strict();

export type SwitchOrgRequest = z.infer<typeof switchOrgRequestSchema>;

/** Same wire shape as the refresh response: a fresh org-scoped session. */
export const switchOrgResponseSchema = tokenRefreshSchema;
export type SwitchOrgResponse = TokenRefresh;

// ─── Auth API client ─────────────────────────────────────────────────────────

export class AuthClientError extends Error {
	readonly code = 'auth_client_error';

	constructor(
		message: string,
		readonly status: number,
		readonly body?: unknown,
	) {
		super(message);
		this.name = 'AuthClientError';
	}
}

export interface AuthClientOptions {
	/**
	 * Transport used for every call. Defaults to same-origin `fetch`, which is
	 * what the agent/crew pattern (worker-served SPA proxying /auth/*) uses.
	 */
	fetchImpl?: typeof fetch;
	/** Base URL prepended to each path (builder's cross-origin API URL). */
	baseUrl?: string;
	/**
	 * Path prefix for auth endpoints. Defaults to `/auth` (agent + crew);
	 * the backend API uses `/api/auth`.
	 */
	pathPrefix?: string;
}

interface UnwrappedJson {
	json: Record<string, unknown> | undefined;
}

/** Accepts both the raw agent/crew envelope and the backend `{ success, data }` envelope. */
async function unwrap(response: Response): Promise<UnwrappedJson> {
	const text = await response.text();
	let parsed: unknown;
	try {
		parsed = text ? JSON.parse(text) : undefined;
	} catch {
		throw new AuthClientError('Auth endpoint returned a non-JSON body.', response.status, text);
	}
	if (parsed === undefined || typeof parsed !== 'object' || parsed === null) {
		return { json: undefined };
	}
	const record = parsed as Record<string, unknown>;
	if (record.success === true && 'data' in record) {
		const data = record.data;
		return {
			json: data !== null && typeof data === 'object'
				? data as Record<string, unknown>
				: undefined,
		};
	}
	return { json: record };
}

function parseOrThrow<T extends z.ZodType>(
	schema: T,
	json: Record<string, unknown> | undefined,
	status: number,
	message: string,
): z.output<T> {
	const result = schema.safeParse(json ?? {});
	if (!result.success) {
		// Include the failing field paths so contract drift is self-diagnosing
		// instead of surfacing as an opaque mismatch.
		const issues = result.error.issues
			.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
			.join('; ');
		throw new AuthClientError(`${message} (${issues})`, status, json);
	}
	return result.data;
}

/**
 * Minimal, dependency-free client for the auth endpoints every Blazorly
 * frontend already exposes. Only the common core (login / refresh / me /
 * logout) is contracted; app-specific flows (registration with email
 * verification, 2FA challenge completion, password reset) stay in each app
 * until the dedicated identity service centralizes them.
 */
export function createAuthClient(options: AuthClientOptions = {}) {
	const doFetch = options.fetchImpl ?? fetch;
	const baseUrl = options.baseUrl?.replace(/\/$/u, '') ?? '';
	const prefix = (options.pathPrefix ?? '/auth').replace(/\/$/u, '');

	async function request(path: string, init?: RequestInit): Promise<Response> {
		// credentials: "include" is required for cross-origin SSO — sibling apps
		// on the shared domain (e.g. builder.blazorly.com calling
		// agent.blazorly.com) must send the Domain=.blazorly.com refresh cookie.
		// Same-origin calls are unaffected.
		const initWithCredentials = { credentials: 'include' as const, ...init };
		let response: Response;
		try {
			response = await doFetch(`${baseUrl}${prefix}${path}`, initWithCredentials);
		} catch (error) {
			throw new AuthClientError('Auth endpoint unreachable.', 0, error);
		}
		if (!response.ok) {
			const { json } = await unwrap(response).catch(() => ({ json: undefined }) as UnwrappedJson);
			// Two error envelopes exist: the backend's `{ error: "message" }` string
			// and the agent worker's `{ error: { code, message } }` object.
			const rawError = json?.error;
			const detail = typeof rawError === 'string'
				? rawError
				: (rawError !== null && typeof rawError === 'object' && typeof (rawError as { message?: unknown }).message === 'string'
					? (rawError as { message: string }).message
					: undefined);
			throw new AuthClientError(detail ?? `Auth endpoint returned ${response.status}.`, response.status, json);
		}
		return response;
	}

	function bearer(accessToken: string): RequestInit {
		return { headers: { Authorization: `Bearer ${accessToken}` } };
	}

	return {
		async login(request_: PasswordLoginRequest): Promise<AuthSession> {
			const response = await request('/login', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify(passwordLoginRequestSchema.parse(request_)),
			});
			const { json } = await unwrap(response);
			return parseOrThrow(authSessionSchema, json, response.status, 'Login response did not match the auth contract.');
		},

		async refresh(): Promise<TokenRefresh> {
			const response = await request('/refresh', { method: 'POST' });
			const { json } = await unwrap(response);
			return parseOrThrow(tokenRefreshSchema, json, response.status, 'Refresh response did not match the auth contract.');
		},

		async switchOrg(accessToken: string, organizationId: string): Promise<SwitchOrgResponse> {
			const response = await request('/switch-org', {
				method: 'POST',
				headers: {
					'content-type': 'application/json',
					...bearer(accessToken).headers,
				},
				body: JSON.stringify(switchOrgRequestSchema.parse({ organizationId })),
			});
			const { json } = await unwrap(response);
			return parseOrThrow(
				switchOrgResponseSchema,
				json,
				response.status,
				'Switch-org response did not match the auth contract.',
			);
		},

		async me(accessToken: string): Promise<AuthenticatedUser> {
			const response = await request('/me', { method: 'GET', ...bearer(accessToken) });
			const { json } = await unwrap(response);
			// Both the raw `{ user }` envelope and a bare user object are accepted.
			const candidate = json && 'user' in json && json.user !== null && typeof json.user === 'object'
				? json.user as Record<string, unknown>
				: json;
			return parseOrThrow(authenticatedUserSchema, candidate, response.status, 'User response did not match the auth contract.');
		},

		async logout(accessToken: string): Promise<void> {
			await request('/logout', { method: 'POST', ...bearer(accessToken) });
		},
	};
}

export type AuthClient = ReturnType<typeof createAuthClient>;
